import { useQuery } from '@tanstack/vue-query';
import { useExperienceApi } from '@/shared/api/useExperienceApi.ts';
import { type MaybeRefOrGetter, unref } from 'vue';

type CityTagSorting = '-popularity' | '-excursions_count' | '-tours_count' | 'ranking_mab';

const DEFAULT_PAGE_SIZE = 1000;

interface Params {
  slug: MaybeRefOrGetter<string>;
  pageSize?: MaybeRefOrGetter<number>;
  sorting?: MaybeRefOrGetter<CityTagSorting>;
  enabled?: MaybeRefOrGetter<boolean>;
}

const setParams = (params: Params): Required<Params> => {
  return {
    slug: params.slug,
    pageSize: params?.pageSize || computed(() => DEFAULT_PAGE_SIZE),
    sorting: params?.sorting || computed(() => '-popularity'),
    enabled: params?.enabled !== undefined ? params?.enabled : computed(() => true),
  };
};

export const useCityTag = (params: Params) => {
  const { slug, pageSize, sorting, enabled } = setParams(params);
  const { getCityTags } = useExperienceApi();

  const { data: cityTags } = useQuery({
    queryKey: ['cityTag', slug, pageSize, sorting],
    queryFn: async () => {
      return await getCityTags({
        query: {
          city__slug: unref(slug) as string,
          page_size: unref(pageSize) as number,
          sorting: unref(sorting) as CityTagSorting,
        },
      });
    },
    enabled: computed(() => !!unref(enabled)),
    placeholderData: (prev) => prev,
    select: (response) => {
      return response.data?.results;
    },
    retry: false,
  });

  return {
    cityTags,
  };
};
